import { useDeleteViewMutation, useUpdateViewMutation } from "@api/view.api";
import { NamedView, PersistableView, View } from "@common/types/views/views";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import classNames from "classnames";
import {
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useViewScope } from "@hooks/view/useViewScope";
import {
    type UseViewCollectionReturn,
    useViewCollection,
} from "@hooks/view/useViewCollection";
import { useViewComparison } from "@hooks/view/useViewComparison";
import { ViewConfig } from "@lib/views";
import mainInterfaceStyles from "@components/mainInterface/MainInterface.module.scss";
import { Button } from "@design-system/Button/Button";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { UpdateViewModal, UpdateViewModalMode } from "./UpdateViewModal";
import { DEFAULT_VIEW_ID } from "../constants";
import {
    ViewLabel,
    ViewsDropdown,
} from "@design-system/ViewToolbar/ViewsDropdown";
import styles from "./ViewToolbar.module.scss";

interface ViewToolbarBaseProps<T extends PersistableView> {
    readonly view: T;
    readonly onViewChange: (view: T) => void;
    /**
     * Called when the user explicitly selects, resets, saves, or deletes a
     * view (i.e. switches to a different view definition). Defaults to
     * `onViewChange`. Pages with separate filter state (bills/invoices) pass a
     * dedicated handler that applies the selected view's filters, so layout
     * edits via `onViewChange` do not clobber page filters.
     */
    readonly onViewSelect?: (view: T) => void;
    readonly viewConfig?: ViewConfig<any, string, string>;
    readonly builtInViews: Array<NamedView<T>>;
    /** Display labels by view id for the views picker; see `ViewLabel`. */
    readonly viewLabels?: ReadonlyMap<string, ViewLabel>;
    readonly defaultView?: NamedView<T>;
    readonly defaultFilters: View["filters"];
    readonly children?: React.ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    /**
     * View a deep link landed on. Marks it as the selected base view without
     * re-applying its filters — the caller has already seeded the view content
     * and any narrower filters the link asked for.
     */
    readonly initialBaseViewId?: string;
    readonly getViewForComparison?: (args: {
        view: T;
        baseView: NamedView<T> | null;
    }) => T;
    /**
     * Hides the column settings, keeping `children` in place. For a page
     * layout whose own controls replace them (Tasks Board's gear).
     */
    readonly isColumnSettingsHidden?: boolean;
}

interface OwnViewCollectionProps {
    /** Omit to keep the picked view for this mount only. */
    readonly baseViewCacheKey?:
        | WORKSPACE_USER_CACHE_KEYS
        | ORGANIZATION_USER_CACHE_KEYS;
    /** See `useViewCollection`'s `fallbackBaseViewId`. */
    readonly fallbackBaseViewId?: string;
}

type ViewToolbarProps<T extends PersistableView> = ViewToolbarBaseProps<T> &
    (
        | (OwnViewCollectionProps & { readonly viewCollection?: never })
        | {
              /**
               * The views and the picked one, from the page's own
               * `useViewCollection`, for a page that reads the picked view
               * itself. The toolbar then keeps none of its own.
               */
              readonly viewCollection: UseViewCollectionReturn<T>;
              readonly baseViewCacheKey?: never;
              readonly fallbackBaseViewId?: never;
          }
    );

type ViewToolbarContentProps<T extends PersistableView> =
    ViewToolbarBaseProps<T> & {
        readonly viewCollection: UseViewCollectionReturn<T>;
    };

const ViewToolbarContent = <T extends PersistableView>({
    view,
    disabled,
    onViewChange,
    onViewSelect,
    viewConfig,
    builtInViews,
    viewLabels,
    defaultView,
    defaultFilters,
    children,
    className,
    initialBaseViewId,
    getViewForComparison,
    isColumnSettingsHidden = false,
    viewCollection: { allViews, baseView, setBaseViewId },
}: ViewToolbarContentProps<T>) => {
    const handleViewSelect = useCallback(
        (selectedView: T) => {
            (onViewSelect ?? onViewChange)(selectedView);
        },
        [onViewSelect, onViewChange],
    );
    const viewScope = useViewScope();

    const hasAppliedInitialBaseViewRef = useRef(false);
    useEffect(() => {
        if (!initialBaseViewId || hasAppliedInitialBaseViewRef.current) {
            return;
        }
        hasAppliedInitialBaseViewRef.current = true;
        setBaseViewId(initialBaseViewId);
    }, [initialBaseViewId, setBaseViewId]);

    const defaultViewWithFallback = useMemo(
        () =>
            defaultView ??
            builtInViews.find((v) => v.id === DEFAULT_VIEW_ID) ??
            builtInViews[0],
        [defaultView, builtInViews],
    );

    const comparisonView = getViewForComparison
        ? getViewForComparison({ view, baseView })
        : view;

    const { isDirty } = useViewComparison({
        view: comparisonView,
        baseView,
        defaultFilters,
    });

    // Defer resolution as we wait for the
    // context to update the view
    const isViewDirty = useDeferredValue(isDirty);

    const savedViews: Array<NamedView<T>> = useMemo(
        () =>
            allViews.filter(
                (v) => !builtInViews.some((builtin) => builtin.id === v.id),
            ),
        [allViews, builtInViews],
    );

    const deleteView = useDeleteViewMutation();
    const updateView = useUpdateViewMutation(viewScope);

    const [modalMode, setModalMode] = useState<UpdateViewModalMode>("save");
    const [modalTargetView, setModalTargetView] = useState<T>();

    const handleCloseViewModal = () => {
        setModalTargetView(undefined);
    };

    const handleResetClick = () => {
        if (baseView) {
            handleViewSelect(baseView);
        }
    };

    const isBuiltInView = (viewToCheck: NamedView<T>) =>
        builtInViews.some((builtInView) => builtInView.id === viewToCheck.id);

    const isExistingCustomView = Boolean(baseView && !isBuiltInView(baseView));

    const handleSaveOrUpdateClick = async () => {
        if (isExistingCustomView && baseView) {
            await updateView.mutateAsync({
                ...view,
                id: baseView.id,
                name: baseView.name,
            });
        } else {
            setModalMode("save");
            setModalTargetView(view);
        }
    };

    const handleViewModalSuccess = (updatedView: NamedView<T>) => {
        setBaseViewId(updatedView.id);
        handleViewSelect(updatedView);
    };

    const handleViewDelete = async (viewToDelete: NamedView<T>) => {
        if (!viewToDelete.id) {
            return;
        }

        if (defaultViewWithFallback) {
            setBaseViewId(defaultViewWithFallback.id);
            handleViewSelect(defaultViewWithFallback);
        } else {
            setBaseViewId(null);
            handleViewSelect({ ...view, filters: defaultFilters });
        }

        await deleteView.mutateAsync(viewToDelete);
    };

    const handleViewRename = (viewToEdit: NamedView<T>) => {
        setModalMode("rename");
        setModalTargetView(viewToEdit);
    };

    const handleViewDuplicate = (viewToDuplicate: NamedView<T>) => {
        setModalMode("duplicate");
        setModalTargetView(viewToDuplicate);
    };

    const handleViewCreate = () => {
        setModalMode("save");
        setModalTargetView(view);
    };

    const handleBaseViewSelect = useCallback(
        (selectedView: NamedView<T> | null) => {
            if (selectedView) {
                setBaseViewId(selectedView.id);
                handleViewSelect(selectedView);
            } else {
                setBaseViewId(null);
            }
        },
        [setBaseViewId, handleViewSelect],
    );

    return (
        <div className={classNames(styles.container, className)}>
            <div
                className={classNames(
                    mainInterfaceStyles.container,
                    styles.toolbar,
                )}
            >
                <div className={styles.viewControls}>
                    <ViewsDropdown
                        builtInViews={builtInViews}
                        savedViews={savedViews}
                        viewLabels={viewLabels}
                        selectedView={baseView}
                        onViewSelect={handleBaseViewSelect}
                        className={styles.viewDropdown}
                        disabled={disabled}
                        onViewDelete={handleViewDelete}
                        onViewRename={handleViewRename}
                        onViewDuplicate={handleViewDuplicate}
                        onViewCreate={handleViewCreate}
                    />
                </div>
                {children}
                {viewConfig && !isColumnSettingsHidden && (
                    <ColumnSettings
                        view={view}
                        onViewChange={onViewChange}
                        viewConfig={viewConfig}
                        defaultView={
                            baseView ?? defaultViewWithFallback ?? view
                        }
                        disabled={disabled}
                    />
                )}
                {isViewDirty && (
                    <>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleSaveOrUpdateClick}
                            disabled={disabled}
                        >
                            {isExistingCustomView ? "Save changes" : "Save"}
                        </Button>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleResetClick}
                            disabled={disabled}
                        >
                            Reset
                        </Button>
                    </>
                )}
            </div>
            {modalTargetView && (
                <UpdateViewModal
                    show={!!modalTargetView}
                    mode={modalMode}
                    viewToSave={modalMode === "save" ? view : modalTargetView}
                    existingView={
                        modalMode === "rename"
                            ? (modalTargetView as NamedView<T>)
                            : undefined
                    }
                    onClose={handleCloseViewModal}
                    onSuccess={handleViewModalSuccess}
                />
            )}
        </div>
    );
};

const ViewToolbarWithOwnCollection = <T extends PersistableView>({
    baseViewCacheKey,
    fallbackBaseViewId,
    ...props
}: ViewToolbarBaseProps<T> & OwnViewCollectionProps) => {
    const viewCollection = useViewCollection({
        viewType: props.view.type,
        builtInViews: props.builtInViews,
        cacheKey: baseViewCacheKey,
        fallbackBaseViewId,
    });
    return <ViewToolbarContent {...props} viewCollection={viewCollection} />;
};

export const ViewToolbar = <T extends PersistableView>(
    props: ViewToolbarProps<T>,
) =>
    props.viewCollection === undefined ? (
        <ViewToolbarWithOwnCollection {...props} />
    ) : (
        <ViewToolbarContent {...props} viewCollection={props.viewCollection} />
    );
